<template>
  <div class="agreement-bill" :class="{ 'agreement-bill--eudora': brand === 'eudora' }">
    <div class="agreement-bill__grid">
      <h6 class="agreement-bill__page-title">Detalhes do acordo</h6>

      <div class="agreement-bill__summary-col">
        <div class="summary-card">
          <div class="summary-card__brand">
            <span class="summary-card__brand-icon-slot" aria-hidden="true">
              <img :src="brandIcon" alt="" class="summary-card__brand-icon" />
            </span>
            <strong class="summary-card__brand-name">{{ brandName }}</strong>
          </div>

          <div class="summary-card__row">
            <span class="summary-card__label">Valor do acordo:</span>
            <strong class="summary-card__value">{{ agreementTotalValueFormatted(contextItem) }}</strong>
          </div>
          <div class="summary-card__row">
            <span class="summary-card__label">Plano:</span>
            <strong class="summary-card__value">{{ planLabel(contextItem) }}</strong>
          </div>
          <div v-if="planLabel(contextItem) === 'À vista'" class="summary-card__row">
            <span class="summary-card__label">{{ valueRowLabel(contextItem) }}</span>
            <strong class="summary-card__value">{{ summaryPaymentValue }}</strong>
          </div>
          <div v-if="planLabel(contextItem) !== 'À vista' && entryFormatted(contextItem)" class="summary-card__row">
            <span class="summary-card__label">Entrada:</span>
            <strong class="summary-card__value">{{ entryFormatted(contextItem) }}</strong>
          </div>
          <div v-if="planLabel(contextItem) !== 'À vista' && installmentsLabel(contextItem)" class="summary-card__row">
            <span class="summary-card__label">Parcelas:</span>
            <strong class="summary-card__value">{{ installmentsLabel(contextItem) }}</strong>
          </div>
          <div class="summary-card__row">
            <span class="summary-card__label">Vencimento:</span>
            <strong class="summary-card__value">{{ summaryDueDate }}</strong>
          </div>
          <div class="summary-card__row">
            <span class="summary-card__label">Forma de pagamento:</span>
            <strong class="summary-card__value">{{ summaryPaymentMethodLabel }}</strong>
          </div>

          <hr class="summary-card__divider" />
          <div class="summary-card__titles-row">
            <span class="summary-card__label">Título(s) negociados:</span>
            <span class="summary-card__titles-value">
              {{ titlesExpanded ? titlesText(contextItem) : truncated(titlesText(contextItem), 20) }}
            </span>
            <button
              v-if="titlesText(contextItem) && titlesText(contextItem).length > 20"
              type="button"
              class="summary-card__btn-expand"
              @click="titlesExpanded = !titlesExpanded"
            >
              <i :class="titlesExpanded ? 'fa fa-chevron-up' : 'fa fa-chevron-down'" />
            </button>
          </div>
        </div>
      </div>

      <div class="agreement-bill__payment-col">
        <p class="agreement-bill__intro">{{ paymentIntro }}</p>

        <div v-if="showMethodPicker" class="method-cards" role="group" aria-label="Forma de pagamento">
          <button
            v-for="(opt, idx) in agreementBillOptions"
            :key="`${opt.key}-${idx}`"
            type="button"
            class="method-card"
            :class="{ 'method-card--selected': idx === agreementBillSelectedOptionIndex }"
            :style="methodCardSelectedStyle(idx)"
            @click="onSelectMethod(idx)"
          >
            <span v-if="idx === agreementBillSelectedOptionIndex" class="method-card__check" aria-hidden="true">
              <i class="fa fa-check" />
            </span>
            <span class="method-card__inner">
              <img
                v-if="opt.key === 'pix'"
                src="/img/pix-icon.svg"
                alt=""
                class="method-card__icon method-card__icon--pix"
              />
              <i
                v-else-if="opt.key === 'card'"
                class="fa fa-credit-card method-card__icon method-card__icon--card"
                aria-hidden="true"
              />
              <i
                v-else-if="opt.key === 'boleto' && opt.boletoSubKey === 'link'"
                class="fa fa-external-link method-card__icon method-card__icon--linkbill"
                aria-hidden="true"
              />
              <i
                v-else
                class="fa fa-barcode method-card__icon method-card__icon--boleto"
                aria-hidden="true"
              />
              <span class="method-card__label">{{ paymentOptionLabel(opt) }}</span>
            </span>
          </button>
        </div>

        <p
          v-if="selectedOption && selectedOption.key === 'boleto' && selectedOption.boletoSubKey !== 'link'"
          class="agreement-bill__helper-text"
        >
          Clique abaixo para copiar o código de barras
        </p>
        <p
          v-else-if="selectedOption && selectedOption.key === 'boleto' && selectedOption.boletoSubKey === 'link'"
          class="agreement-bill__helper-text"
        >
          Clique abaixo para baixar o boleto
        </p>

        <div v-if="selectedOption && selectedOption.key === 'pix'" class="agreement-bill__payment-block">
          <div class="action-box" role="button" tabindex="0" @click="copyPixText(selectedOption.pixCopyAndPast)">
            <span class="action-box__text">{{ selectedOption.pixCopyAndPast }}</span>
            <i class="fa fa-clone action-box__icon" aria-hidden="true" />
          </div>
          <p class="agreement-bill__qr-label">Escaneie o QR Code</p>
          <div class="agreement-bill__qr-wrap">
            <qrcode-vue :value="selectedOption.pixCopyAndPast" :size="200" level="H" />
          </div>
          <button
            v-if="selectedOptionHasPdf"
            type="button"
            class="agreement-bill__download-bill"
            :disabled="downloadingBillPdf"
            @click="downloadBillPdf"
          >
            <i class="fa fa-download mr-2" aria-hidden="true" />
            {{ downloadingBillPdf ? 'BAIXANDO...' : 'BAIXAR BOLETO' }}
          </button>
        </div>

        <div v-else-if="selectedOption && selectedOption.key === 'boleto'" class="agreement-bill__payment-block">
          <div
            v-if="selectedOption.digitableLine"
            class="action-box"
            :class="{ 'action-box--copied': barcodeCopiedFeedbackVisible }"
            role="button"
            tabindex="0"
            @click="copyBarcodeText(selectedOption.digitableLine)"
          >
            <span class="action-box__text">{{ selectedOption.digitableLine }}</span>
            <i class="fa fa-clone action-box__icon" aria-hidden="true" />
            <span v-if="barcodeCopiedFeedbackVisible" class="action-box__copied-feedback">Código copiado</span>
          </div>
          <button
            v-if="selectedOptionCanDownloadBill"
            type="button"
            class="agreement-bill__download-bill"
            :disabled="downloadingBillPdf"
            @click="downloadBillPdf"
          >
            <i class="fa fa-download mr-2" aria-hidden="true" />
            {{ downloadingBillPdf ? 'BAIXANDO...' : 'BAIXAR BOLETO' }}
          </button>
        </div>

        <div v-else-if="selectedOption && selectedOption.key === 'card'" class="agreement-bill__payment-block">
          <div
            class="action-box action-box--link"
            role="button"
            tabindex="0"
            @click="openCardLink(selectedOption.linkCard)"
          >
            <span class="action-box__text">{{ selectedOption.linkCard }}</span>
            <i class="fa fa-external-link action-box__icon" aria-hidden="true" />
          </div>
        </div>

        <div v-if="showWhatsappBoletoInvite" class="agreement-bill__whatsapp-below">
          <div class="whatsapp-box">
            <p class="whatsapp-box__label">Deseja receber o boleto pelo WhatsApp?</p>
            <button class="whatsapp-box__button" type="button" @click="openWhatsappModal">
              <i class="fa fa-whatsapp mr-2" aria-hidden="true" />Quero receber
            </button>
          </div>
        </div>

        <button type="button" class="agreement-bill__back" @click="onBack">
          Voltar para as pendências
        </button>
      </div>
    </div>

    <div v-if="showWhatsappModal" class="agreement-modal agreement-modal--backdrop" aria-modal="true" role="dialog">
      <div class="agreement-modal__card agreement-modal__card--form">
        <button type="button" class="agreement-modal__close" aria-label="Fechar" @click="closeWhatsappModal">
          <i class="fa fa-times" aria-hidden="true" />
        </button>
        <h3 class="agreement-modal__title agreement-modal__title--left">Boleto por WhatsApp</h3>

        <template v-if="hasStoredPhone">
          <p class="agreement-modal__hint">Receba pelo número abaixo</p>
          <label class="agreement-modal__phone-box">
            <input
              v-model="useSavedNumber"
              type="checkbox"
              class="agreement-modal__checkbox"
              @change="onUseSavedNumberChange"
            />
            <strong class="agreement-modal__phone-text">{{ storedPhoneDisplay }}</strong>
          </label>
          <p class="agreement-modal__info">
            <span class="agreement-modal__info-icon" aria-hidden="true">!</span>
            <span class="agreement-modal__info-text">Você receberá uma mensagem pelo WhatsApp com o seu boleto de pagamento.</span>
          </p>
        </template>

        <p class="agreement-modal__hint" :class="{ 'agreement-modal__hint--spaced': hasStoredPhone }">
          Ou informe outro número de WhatsApp
        </p>
        <input
          v-model="otherWhatsappPhone"
          type="tel"
          class="agreement-modal__input"
          placeholder="Ex: (41) 99999-9999"
          autocomplete="tel"
          @input="onOtherWhatsappInput"
        />

        <button
          type="button"
          class="agreement-modal__cta"
          :disabled="!canSubmitWhatsapp || whatsappSending"
          @click="submitWhatsapp"
        >
          {{ whatsappSending ? 'ENVIANDO…' : 'RECEBER BOLETO' }}
        </button>
      </div>
    </div>

    <div v-if="showWhatsappResultModal" class="agreement-modal agreement-modal--backdrop" aria-modal="true" role="dialog">
      <div class="agreement-modal__card agreement-modal__card--result">
        <button type="button" class="agreement-modal__close" aria-label="Fechar" @click="closeWhatsappResultModal">
          <i class="fa fa-times" aria-hidden="true" />
        </button>

        <template v-if="whatsappResultSuccess">
          <div class="agreement-modal__result-icon agreement-modal__result-icon--ok" aria-hidden="true">
            <i class="fa fa-check" />
          </div>
          <h3 class="agreement-modal__title agreement-modal__title--center">Boleto enviado com sucesso!</h3>
          <p class="agreement-modal__body agreement-modal__body--center">
            O boleto foi enviado para o número de WhatsApp informado
          </p>
          <button type="button" class="agreement-modal__cta agreement-modal__cta--block" @click="closeWhatsappResultModal">
            OK
          </button>
        </template>

        <template v-else>
          <div class="agreement-modal__result-icon agreement-modal__result-icon--err" aria-hidden="true">
            <i class="fa fa-exclamation" />
          </div>
          <h3 class="agreement-modal__title agreement-modal__title--center">Falha ao enviar boleto!</h3>
          <p class="agreement-modal__body agreement-modal__body--center">
            Ocorreu um erro ao enviar o boleto. Por favor, tente novamente.
          </p>
          <button type="button" class="agreement-modal__link-back" @click="closeWhatsappResultModal">Voltar</button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import QrcodeVue from 'qrcode.vue';
import { formatData } from '@/utils/helpers';
import { brandAccentColor } from '@/utils/brandColors';
import flashMessages from '@/utils/flash-messages';

export default {
  name: 'AgreementBillDetail',
  components: { QrcodeVue },
  data: () => ({
    titlesExpanded: false,
    showWhatsappModal: false,
    showWhatsappResultModal: false,
    whatsappResultSuccess: false,
    whatsappSending: false,
    useSavedNumber: true,
    otherWhatsappPhone: '',
    storedPhoneDigits: '',
    barcodeCopiedFeedbackVisible: false,
    barcodeCopiedFeedbackTimeoutId: null,
    downloadingBillPdf: false,
  }),
  computed: {
    ...mapState('negotiation', [
      'agreementBillBrand',
      'agreementBillContextItem',
      'agreementBillOptions',
      'agreementBillSelectedOptionIndex',
    ]),
    brand() {
      return this.agreementBillBrand === 'eudora' ? 'eudora' : 'boticario';
    },
    brandName() {
      return this.brand === 'eudora' ? 'Eudora' : 'Boticário';
    },
    brandIcon() {
      return this.brand === 'eudora' ? '/img/eudora-icone-card.png' : '/img/boticario-icone-card.png';
    },
    contextItem() {
      return this.agreementBillContextItem && typeof this.agreementBillContextItem === 'object'
        ? this.agreementBillContextItem
        : {};
    },
    selectedOption() {
      const opts = this.agreementBillOptions || [];
      const i = Number(this.agreementBillSelectedOptionIndex) || 0;
      return opts[i] || null;
    },
    showMethodPicker() {
      return (this.agreementBillOptions || []).length > 1;
    },
    paymentIntro() {
      const sel = this.selectedOption;
      if (!sel) return '';
      if (this.showMethodPicker) return 'Escolha como deseja pagar:';
      if (sel.key === 'pix') {
        return 'Copie a chave PIX ou escaneie o QR Code para realizar o pagamento.';
      }
      if (sel.key === 'card') {
        return 'Acesse o link abaixo para pagar com cartão de crédito.';
      }
      return 'Copie os dados do boleto ou acesse o link do boleto para realizar o pagamento.';
    },
    summaryPaymentValue() {
      const sel = this.selectedOption;
      if (sel && sel.key === 'boleto' && sel.billValue != null && String(sel.billValue).trim() !== '') {
        const v = String(sel.billValue).trim();
        if (v.startsWith('R$')) return v;
        return `R$ ${v}`;
      }
      return this.paymentValueFormatted(this.contextItem);
    },
    summaryDueDate() {
      const sel = this.selectedOption;
      if (sel && sel.key === 'boleto' && sel.billDueDate != null && String(sel.billDueDate).trim() !== '') {
        return String(sel.billDueDate).trim();
      }
      const raw = this.contextItem.earliestDueDate || this.contextItem.dueDate;
      if (!raw) return '—';
      return formatData(raw, 'YYYY-MM-DD', 'L');
    },
    summaryPaymentMethodLabel() {
      return this.selectedOption ? this.selectedOption.label : '—';
    },
    showWhatsappBoletoInvite() {
      const sel = this.selectedOption;
      if (!sel || (sel.key !== 'boleto' && sel.key !== 'pix' && sel.key !== 'card')) return false;
      const id = this.contextItem.agreementId;
      return id != null && String(id).trim() !== '';
    },
    whatsappFrom() {
      return this.brand === 'eudora' ? 'Eudora' : 'Boticario';
    },
    hasStoredPhone() {
      return this.storedPhoneDigits.length >= 10;
    },
    storedPhoneDisplay() {
      return this.formatPhoneBR(this.storedPhoneDigits);
    },
    canSubmitWhatsapp() {
      const other = String(this.otherWhatsappPhone || '').replace(/\D/g, '');
      if (other.length >= 10) return true;
      if (this.useSavedNumber && this.hasStoredPhone) return true;
      return false;
    },
    selectedOptionBillPdfBase64() {
      const raw = String(this.selectedOption?.billPdf || '').trim();
      if (!raw) return '';
      return raw
        .replace(/^data:application\/pdf;base64,/i, '')
        .replace(/\s+/g, '');
    },
    selectedOptionPixPdfBase64() {
      const raw = String(this.selectedOption?.pixPdf || '').trim();
      if (!raw) return '';
      return raw
        .replace(/^data:application\/pdf;base64,/i, '')
        .replace(/\s+/g, '');
    },
    selectedOptionHasPdf() {
      return this.selectedOptionBillPdfBase64.length > 0 || this.selectedOptionPixPdfBase64.length > 0;
    },
    selectedOptionCanDownloadBill() {
      if (!this.selectedOption || this.selectedOption.key !== 'boleto') return false;
      const hasLinkBill = String(this.selectedOption.linkBill || '').trim().length > 0;
      return this.selectedOptionHasPdf || hasLinkBill;
    },
    selectedOptionPdfBase64ForDownload() {
      if (!this.selectedOption) return '';
      if (this.selectedOption.key === 'pix') {
        return this.selectedOptionPixPdfBase64 || this.selectedOptionBillPdfBase64;
      }
      return this.selectedOptionBillPdfBase64 || this.selectedOptionPixPdfBase64;
    },
  },
  methods: {
    ...mapActions('negotiation', [
      'ActionNegotiationSetAgreementBillPaymentOption',
      'ActionNegotiationGoToDashboard',
      'ActionNegotiationSendEmailOrWhatsapp',
      'ActionNegotiationLogPaymentAction',
    ]),
    accent() {
      return brandAccentColor(this.brand);
    },
    methodCardSelectedStyle(idx) {
      if (idx !== this.agreementBillSelectedOptionIndex) return {};
      return {
        borderColor: this.accent(),
        boxShadow: `0 0 0 1px ${this.accent()}`,
      };
    },
    onSelectMethod(idx) {
      this.ActionNegotiationSetAgreementBillPaymentOption(idx);
    },
    paymentOptionLabel(opt) {
      if (opt && opt.key === 'boleto' && opt.boletoSubKey !== 'link') return 'Boleto';
      return opt?.label || '—';
    },
    planLabel(item) {
      const currentInstallmentNumber = Number(item.currentInstallmentNumber);
      const installmentNumberAgreement = Number(item.installmentNumberAgreement);
      if (!Number.isNaN(currentInstallmentNumber) && !Number.isNaN(installmentNumberAgreement)) {
        if (currentInstallmentNumber === 0 && installmentNumberAgreement === 0) return 'À vista';
        return 'Parcelado';
      }
      const n = String(item.formName || '').toUpperCase();
      if (n.includes('PARCEL')) return 'Parcelado';
      if (n.includes('VISTA') || n.includes('À VISTA') || n.includes('A VISTA')) return 'À vista';
      const num = Number(item.installmentNumber);
      if (!Number.isNaN(num) && num > 1) return 'Parcelado';
      return 'À vista';
    },
    valueRowLabel(item) {
      return this.planLabel(item) === 'À vista' ? 'Valor do pagamento:' : 'Valor atualizado:';
    },
    paymentValueFormatted(item) {
      const f = item.updatedValueFormatted != null ? String(item.updatedValueFormatted).trim() : '';
      if (f) return `R$ ${f}`;
      if (item.updatedValue != null && !Number.isNaN(Number(item.updatedValue))) {
        return Number(item.updatedValue).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      }
      return '—';
    },
    agreementTotalValueFormatted(item) {
      const installmentNumberAgreement = Number(item.installmentNumberAgreement);
      const updatedValue = Number(item.updatedValue);
      if (!Number.isNaN(updatedValue) && !Number.isNaN(installmentNumberAgreement) && installmentNumberAgreement > 1) {
        return (updatedValue * installmentNumberAgreement).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
      }
      return this.paymentValueFormatted(item);
    },
    entryFormatted(item) {
      if (this.planLabel(item) !== 'À vista') {
        const totalInstallments = Number(item.installmentNumberAgreement);
        if (!Number.isNaN(totalInstallments) && totalInstallments > 0) {
          return this.paymentValueFormatted(item);
        }
      }
      const v = item.entryValueFormatted ?? item.minimumEntryValueFormatted;
      if (v == null || v === '') return '';
      const s = String(v).trim();
      if (s.startsWith('R$')) return s;
      return `R$ ${s}`;
    },
    installmentsLabel(item) {
      if (this.planLabel(item) !== 'À vista') {
        const installmentNumberAgreement = Number(item.installmentNumberAgreement);
        const installmentNumber = Number(item.installmentNumber);
        const installmentValueFormatted = item.installmentValueFormatted;
        const installmentsCount = (!Number.isNaN(installmentNumberAgreement) && installmentNumberAgreement > 1)
          ? installmentNumberAgreement
          : ((!Number.isNaN(installmentNumber) && installmentNumber > 0) ? installmentNumber : 0);
        if (installmentsCount > 0) {
          const installmentValue = installmentValueFormatted != null && String(installmentValueFormatted).trim() !== ''
            ? `R$ ${String(installmentValueFormatted).replace(/^R\$\s?/i, '')}`
            : this.paymentValueFormatted(item);
          return `+ ${installmentsCount}x de ${installmentValue}`;
        }
      }
      const n = item.installmentNumber;
      const iv = item.installmentValueFormatted;
      if (n != null && iv != null) return `${n}x de R$ ${String(iv).replace(/^R\$\s?/i, '')}`;
      return '';
    },
    titlesText(item) {
      return item.installments || item.contracts || '';
    },
    truncated(text, length) {
      const s = String(text || '');
      if (s.length <= length) return s;
      return `${s.substring(0, length)}...`;
    },
    linkBillDisplay(url) {
      return String(url || '').replace(/^https?:\/\//i, '');
    },
    async copyText(text) {
      const t = String(text || '');
      if (!t) return false;
      try {
        await navigator.clipboard.writeText(t);
        flashMessages.success('Código copiado com sucesso', 'Sucesso', 1200);
        return true;
      } catch {
        flashMessages.error('Não foi possível copiar. Selecione o texto manualmente.', 'Erro', 2500);
        return false;
      }
    },
    logPaymentAction(action, paymentValue) {
      if (!['boticario', 'eudora'].includes(this.brand)) return;
      const paymentMethodByKey = {
        pix: 'P',
        boleto: 'B',
        card: 'L',
      };
      this.ActionNegotiationLogPaymentAction({
        brand: this.brand,
        action,
        agreementId: this.contextItem.agreementId,
        prepareToSetAgreement: this.contextItem,
        paymentMethod: paymentMethodByKey[this.selectedOption?.key] || '',
        paymentValue,
      }).catch((err) => {
        console.warn('[agreement-bill] Log ação de pagamento Boticário ignorado:', err);
      });
    },
    async copyPixText(text) {
      const copied = await this.copyText(text);
      if (copied) this.logPaymentAction('copy_pix', text);
    },
    async copyBarcodeText(text) {
      const copied = await this.copyText(text);
      if (!copied) return;
      this.logPaymentAction('copy_digitable_line', text);
      this.barcodeCopiedFeedbackVisible = true;
      if (this.barcodeCopiedFeedbackTimeoutId) clearTimeout(this.barcodeCopiedFeedbackTimeoutId);
      this.barcodeCopiedFeedbackTimeoutId = setTimeout(() => {
        this.barcodeCopiedFeedbackVisible = false;
        this.barcodeCopiedFeedbackTimeoutId = null;
      }, 3500);
    },
    openCardLink(url) {
      const u = String(url || '').trim();
      if (!u) return;
      this.logPaymentAction('click_card_link', u);
      window.open(u, '_blank', 'noopener,noreferrer');
    },
    base64ToPdfBlob(base64) {
      if (!base64) throw new Error('Boleto indisponível para download.');
      if (!/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) {
        throw new Error('Formato do boleto inválido.');
      }
      let binary = '';
      try {
        binary = window.atob(base64);
      } catch (e) {
        throw new Error('Não foi possível decodificar o boleto.');
      }
      if (!binary.startsWith('%PDF-')) {
        throw new Error('Arquivo de boleto inválido.');
      }
      const chunkSize = 1024;
      const byteArrays = [];
      for (let offset = 0; offset < binary.length; offset += chunkSize) {
        const slice = binary.slice(offset, offset + chunkSize);
        const bytes = new Array(slice.length);
        for (let i = 0; i < slice.length; i += 1) bytes[i] = slice.charCodeAt(i);
        byteArrays.push(new Uint8Array(bytes));
      }
      return new Blob(byteArrays, { type: 'application/pdf' });
    },
    downloadBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    },
    downloadLinkBill(url, filename) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async downloadBillPdf() {
      if (this.downloadingBillPdf) return;
      this.downloadingBillPdf = true;
      try {
        const agreementId = String(this.contextItem?.agreementId || '').trim();
        const filename = agreementId ? `boleto-acordo-${agreementId}.pdf` : 'boleto-acordo.pdf';
        const base64 = this.selectedOptionPdfBase64ForDownload;
        if (base64) {
          const blob = this.base64ToPdfBlob(base64);
          this.downloadBlob(blob, filename);
          flashMessages.success('Boleto baixado com sucesso.', 'Sucesso', 1800);
          if (this.brand === 'boticario') {
            this.logPaymentAction(
              'download_boleto',
              this.selectedOption?.billBarcode || this.selectedOption?.digitableLine || agreementId || filename
            );
          }
          return;
        }
        const linkBill = String(this.selectedOption?.linkBill || '').trim();
        if (!linkBill) throw new Error('Boleto indisponível para download.');
        try {
          const response = await fetch(linkBill);
          if (!response.ok) throw new Error('Não foi possível baixar o boleto pelo link.');
          const blob = await response.blob();
          this.downloadBlob(blob, filename);
        } catch (e) {
          this.downloadLinkBill(linkBill, filename);
        }
        flashMessages.success('Boleto baixado com sucesso.', 'Sucesso', 1800);
        if (this.brand === 'boticario') {
          this.logPaymentAction(
            'download_boleto',
            this.selectedOption?.billBarcode || this.selectedOption?.digitableLine || linkBill || agreementId || filename
          );
        }
      } catch (err) {
        flashMessages.error(err.message || 'Não foi possível baixar o boleto.', 'Erro', 3000);
      } finally {
        this.downloadingBillPdf = false;
      }
    },
    async onBack() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      await this.ActionNegotiationGoToDashboard();
    },
    refreshStoredWhatsappPhone() {
      let u = null;
      try {
        u = JSON.parse(localStorage.getItem('user') || 'null');
      } catch (e) {
        u = null;
      }
      const raw = u?.PhoneNumber != null ? u.PhoneNumber : u?.MobileNumber;
      this.storedPhoneDigits = String(raw || '').replace(/\D/g, '');
    },
    formatPhoneBR(digits) {
      const d = String(digits || '').replace(/\D/g, '');
      if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)} ${d.slice(7)}`;
      if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return d || '—';
    },
    onUseSavedNumberChange() {
      if (this.useSavedNumber) this.otherWhatsappPhone = '';
    },
    onOtherWhatsappInput() {
      const d = String(this.otherWhatsappPhone || '').replace(/\D/g, '');
      if (d.length > 0) this.useSavedNumber = false;
    },
    openWhatsappModal() {
      this.refreshStoredWhatsappPhone();
      this.otherWhatsappPhone = '';
      this.useSavedNumber = this.storedPhoneDigits.length >= 10;
      this.showWhatsappModal = true;
    },
    closeWhatsappModal() {
      if (this.whatsappSending) return;
      this.showWhatsappModal = false;
    },
    closeWhatsappResultModal() {
      this.showWhatsappResultModal = false;
    },
    async submitWhatsapp() {
      if (!this.canSubmitWhatsapp || this.whatsappSending) return;
      const other = String(this.otherWhatsappPhone || '').replace(/\D/g, '');
      const phone = other.length >= 10 ? other : (this.useSavedNumber ? this.storedPhoneDigits : '');
      if (phone.length < 10) return;

      const agreementId = this.contextItem.agreementId;
      if (!agreementId || String(agreementId).trim() === '') return;

      this.whatsappSending = true;
      try {
        await this.ActionNegotiationSendEmailOrWhatsapp({
          sendEmail: false,
          sendWhatsapp: true,
          from: this.whatsappFrom,
          phoneNumber: phone,
          agreementId: String(agreementId).trim(),
        });
        this.showWhatsappModal = false;
        this.whatsappResultSuccess = true;
        this.showWhatsappResultModal = true;
      } catch (err) {
        this.showWhatsappModal = false;
        this.whatsappResultSuccess = false;
        this.showWhatsappResultModal = true;
      } finally {
        this.whatsappSending = false;
      }
    },
  },
  beforeDestroy() {
    if (this.barcodeCopiedFeedbackTimeoutId) clearTimeout(this.barcodeCopiedFeedbackTimeoutId);
  },
};
</script>

<style scoped>
.agreement-bill {
  padding-top: 8px;
  padding-bottom: 32px;
  min-height: calc(100dvh - 140px);
}

.agreement-bill__grid {
  display: block;
  max-width: 420px;
  margin: 0 auto;
  width: 100%;
}

.agreement-bill__page-title {
  font-family: 'Inter', sans-serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #333438;
  margin: 12px 0 16px;
}

.summary-card {
  border: none;
  border-top: 4px solid #06a472;
  border-radius: 20px;
  padding: 16px 16px 12px;
  background-color: #fff;
  box-shadow: var(--negotiation-card-shadow, 0 2px 12px rgba(0, 0, 0, 0.08));
  margin-bottom: 16px;
}

.agreement-bill--eudora .summary-card {
  border-top-color: #613a53;
}

.summary-card__brand {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.summary-card__brand-icon-slot {
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.summary-card__brand-icon {
  width: 100%;
  height: 100%;
  max-width: 32px;
  max-height: 32px;
  object-fit: contain;
  object-position: center;
  display: block;
}

.summary-card__brand-name {
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  color: #333438;
}

.summary-card__row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
  margin-bottom: 6px;
}

.summary-card__label {
  color: #555;
  white-space: nowrap;
}

.summary-card__value {
  font-weight: 700;
  color: #333438;
}

.summary-card__divider {
  margin: 6px 0;
  border-color: #e2e2e2;
}

.summary-card__titles-row {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
}

.summary-card__titles-value {
  flex: 1;
  min-width: 0;
  color: #333438;
  line-height: 1.4;
  word-break: break-word;
}

.summary-card__btn-expand {
  border: none;
  background: transparent;
  color: #176e53;
  padding: 0 2px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
}

.agreement-bill--eudora .summary-card__btn-expand {
  color: #613a53;
}

.summary-card__btn-expand:focus {
  outline: none;
}

.agreement-bill__intro {
  font-family: 'Inter', sans-serif;
  font-size: 0.98rem;
  color: #333438;
  line-height: 1.25;
  margin: 0 0 14px;
}

.agreement-bill__helper-text {
  margin: 0 0 10px;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
}

.method-cards {
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-bottom: 18px;
}

.method-card {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  min-height: 72px;
  padding: 12px 10px;
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 6px 14px -4px rgba(0, 0, 0, 0.1);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.method-card:focus {
  outline: none;
}

.method-card--selected {
  border-width: 2px;
}

.method-card__check {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #06a472;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.agreement-bill--eudora .method-card__check {
  background: #613a53;
}

.method-card__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
}

.method-card__icon {
  font-size: 1.35rem;
  color: #1e1e24;
  flex-shrink: 0;
}

.method-card__icon--card {
  color: #1a6fd4;
}

.method-card--selected .method-card__icon--card {
  color: #1a6fd4;
}

.method-card__icon--boleto {
  color: #333438;
}

.method-card__icon--linkbill {
  color: #1a6fd4;
}

.method-card__icon--pix {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.method-card__label {
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e1e24;
}

.agreement-bill__payment-block {
  margin-bottom: 8px;
}

.action-box {
  width: 100%;
  border: 1px solid #a4a4a4;
  border-radius: 8px;
  background: #ececec;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  margin-bottom: 10px;
  position: relative;
}

.action-box__text {
  flex: 1;
  min-width: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.82rem;
  color: #454545;
  line-height: 1.35;
  word-break: break-all;
}

.action-box__icon {
  color: #505050;
  font-size: 1rem;
}

.action-box--copied {
  background: #e3e3e3;
}

.action-box__copied-feedback {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: rgba(236, 236, 236, 0.58);
  backdrop-filter: blur(1.8px);
  -webkit-backdrop-filter: blur(1.8px);
  color: #2f2f2f;
  font-family: 'Inter', sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  pointer-events: none;
}

.action-box--link .action-box__text {
  word-break: break-word;
}

.agreement-bill__download-bill {
  width: 100%;
  margin-top: 12px;
  border: 1px solid #06a472;
  border-radius: 8px;
  background: #fff;
  color: #06a472;
  min-height: 44px;
  font-family: 'Inter', sans-serif;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  transition: background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.agreement-bill__download-bill:hover:not(:disabled) {
  background: rgba(6, 164, 114, 0.08);
}

.agreement-bill__download-bill:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.agreement-bill--eudora .agreement-bill__download-bill {
  border-color: #613a53;
  color: #613a53;
}

.agreement-bill--eudora .agreement-bill__download-bill:hover:not(:disabled) {
  background: rgba(97, 58, 83, 0.08);
}

.agreement-bill__qr-label {
  margin: 16px 0 8px;
  text-align: center;
  font-family: 'Inter', sans-serif;
  color: #555;
}

.agreement-bill__qr-wrap {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.agreement-bill__back {
  display: block;
  margin: 20px auto 0;
  padding: 0;
  border: none;
  background: none;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #06a472;
  text-decoration: underline;
  cursor: pointer;
}

.agreement-bill--eudora .agreement-bill__back {
  color: #613a53;
}

.agreement-bill__whatsapp-below {
  margin-top: 14px;
}

.agreement-bill__whatsapp-below .whatsapp-box {
  margin-top: 0;
}

.whatsapp-box {
  margin-top: 14px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  padding: 12px;
}

.whatsapp-box__label {
  margin: 0 0 10px;
  text-align: center;
  font-family: 'Inter', sans-serif;
  color: #555;
}

.whatsapp-box__button {
  width: 100%;
  max-width: 220px;
  display: block;
  margin: 0 auto;
  background: #1d9473;
  color: #fff;
  border: none;
  border-radius: 8px;
  min-height: 36px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  cursor: pointer;
}

.agreement-modal--backdrop {
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;
}

.agreement-modal__card {
  position: relative;
  width: 100%;
  max-width: 400px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 22px 20px 20px;
  box-sizing: border-box;
}

.agreement-modal__card--result {
  padding-top: 28px;
  text-align: center;
}

.agreement-modal__close {
  position: absolute;
  top: 12px;
  right: 14px;
  border: none;
  background: transparent;
  color: #888;
  font-size: 1.25rem;
  line-height: 1;
  padding: 6px;
  cursor: pointer;
}

.agreement-modal__close:focus {
  outline: none;
}

.agreement-modal__title {
  font-family: 'Inter', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: #333438;
  margin: 0 0 16px;
  padding-right: 28px;
}

.agreement-modal__title--left {
  text-align: left;
}

.agreement-modal__title--center {
  text-align: center;
  padding-right: 0;
  margin-bottom: 12px;
}

.agreement-modal__hint {
  font-family: 'Inter', sans-serif;
  font-size: 0.88rem;
  color: #666;
  margin: 0 0 8px;
  text-align: left;
}

.agreement-modal__hint--spaced {
  margin-top: 18px;
}

.agreement-modal__phone-box {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #ececec;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  cursor: pointer;
  text-align: left;
}

.agreement-modal__checkbox {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  cursor: pointer;
}

.agreement-modal__phone-text {
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  color: #333438;
}

.agreement-modal__info {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 0.78rem;
  line-height: 1.4;
  color: #555;
  margin: 0 0 4px;
  text-align: left;
}

.agreement-modal__info-icon {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #06a472;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  margin-top: 1px;
}

.agreement-modal__info-text {
  font-style: italic;
}

.agreement-modal__input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #c8c8c8;
  border-radius: 8px;
  padding: 12px 14px;
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  margin-bottom: 18px;
}

.agreement-modal__input::placeholder {
  color: #aaa;
}

.agreement-modal__cta {
  width: 100%;
  border: none;
  border-radius: 10px;
  background: #06a472;
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  min-height: 48px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.agreement-modal__cta:hover:not(:disabled) {
  background: #05966a;
}

.agreement-modal__cta:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.agreement-modal__cta--block {
  margin-top: 8px;
  background: #15795b;
}

.agreement-modal__cta--block:hover:not(:disabled) {
  background: #125c44;
}

.agreement-modal__result-icon {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  margin: 0 auto 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #fff;
}

.agreement-modal__result-icon--ok {
  background: #06a472;
}

.agreement-modal__result-icon--err {
  background: #b3261e;
}

.agreement-modal__body {
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  color: #555;
  line-height: 1.45;
  margin: 0 0 20px;
}

.agreement-modal__body--center {
  text-align: center;
  max-width: 280px;
  margin-left: auto;
  margin-right: auto;
}

.agreement-modal__link-back {
  border: none;
  background: none;
  color: #15795b;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 8px;
}

.agreement-bill--eudora .agreement-modal__info-icon {
  background: #613a53;
}

.agreement-bill--eudora .agreement-modal__result-icon--ok {
  background: #613a53;
}

.agreement-bill--eudora .agreement-modal__cta:not(.agreement-modal__cta--block) {
  background: #613a53;
}

.agreement-bill--eudora .agreement-modal__cta:not(.agreement-modal__cta--block):hover:not(:disabled) {
  background: #4d2e42;
}

.agreement-bill--eudora .agreement-modal__cta--block {
  background: #613a53;
}

.agreement-bill--eudora .agreement-modal__cta--block:hover:not(:disabled) {
  background: #4d2e42;
}

.agreement-bill--eudora .agreement-modal__link-back {
  color: #613a53;
}

@media (min-width: 768px) {
  .agreement-bill__grid {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    column-gap: 32px;
    row-gap: 14px;
    align-items: start;
    max-width: 100%;
    margin: 0;
  }

  .agreement-bill__page-title {
    grid-column: 1 / -1;
    grid-row: 1;
    margin-top: 0;
  }

  .agreement-bill__summary-col {
    grid-column: 1;
    grid-row: 2;
  }

  .agreement-bill__payment-col {
    grid-column: 2;
    grid-row: 2;
  }

  .summary-card {
    margin-bottom: 0;
  }

  .agreement-bill__whatsapp-below {
    margin-top: 0;
    margin-bottom: 8px;
  }
}
</style>
